import { memo, useCallback, useContext, useRef } from "react";
import Box from "@mui/material/Box";
import MuiDialog from "@mui/material/Dialog";
import DialogTitle from "components/Dialog/components/DialogTitle";
import { DialogContent } from "components/Dialog";
import Typography from "components/Typography";
import Button from "components/Button";
import DownloadIcon from "components/Icons/DownloadIcon";
import MuxPlayer from "@mux/mux-player-react/lazy";
import useQuery from "hooks/useQuery";
import RemoteVideoService from "services/RemoteVideoService";
import { useTranslation } from "react-i18next";
import type { MuxMeasurementVideo } from "types";
import useRemoteVideoDownloader from "hooks/useRemoteVideoDownloader";
import { useTheme } from "@mui/material";
import type MuxPlayerElement from "@mux/mux-player";
import DeleteExerciseRecordingVideoAction from "./DeleteExerciseRecordingVideoAction";
import MeasurementService from "services/MeasurementService";
import { useInfoMessage } from "hooks";
import MeasurementsContext from "contexts/MeasurementsContext";

interface ExerciseRecordingVideoDialogProps {
  measurementId: string;
  video: MuxMeasurementVideo;
  open: boolean;
  handleOnClose: () => void;
}

function ExerciseRecordingVideoDialog(props: ExerciseRecordingVideoDialogProps) {
  const { open, handleOnClose, measurementId, video } = props;

  const errorMessage = useInfoMessage({ type: "error" });
  const videoRef = useRef<MuxPlayerElement | null>(null);
  const { t } = useTranslation();
  const theme = useTheme();
  const { handleOnRemoteVideoDownload, status, error } = useRemoteVideoDownloader();

  const measurementContext = useContext(MeasurementsContext);
  const [, actions] = measurementContext || [];

  const thumbnailToken = useQuery(["RemoteVideoService.getThumbnailToken", video.playbackId], ([_, id]) =>
    RemoteVideoService.getThumbnailToken(id)
  );

  const videoToken = useQuery(["RemoteVideoService.getVideoToken", video.playbackId], ([_, id]) =>
    RemoteVideoService.getVideoToken(id)
  );

  const onDownloadVideo = useCallback(() => {
    if (!videoToken.data) return;
    handleOnRemoteVideoDownload(video, videoToken.data);
  }, [handleOnRemoteVideoDownload, video, videoToken.data]);

  const onDeleteVideo = useCallback(async () => {
    /**
     * If the actions are available it means we are in the context of the measurements table.
     * Otherwise, use the MeasurementService to delete the video directly.
     */

    if (actions) {
      return actions.deleteVideo(measurementId);
    }

    try {
      await MeasurementService.deleteVideo(measurementId);
    } catch (error) {
      errorMessage({ message: t("errors.failed_to_delete_video") });
    }
  }, [actions, errorMessage, measurementId, t]);

  return (
    <MuiDialog
      aria-describedby="exercise-recording-video-dialog"
      slotProps={{ paper: { sx: { borderRadius: "20px", width: "100%", maxWidth: 840 } } }}
      sx={{ "& .MuiDialogContent-root": { width: "100%", maxWidth: 840, minWidth: 540 } }}
      data-testid="exercise-recording-video-dialog"
      open={open}
      onClose={handleOnClose}
    >
      <DialogTitle
        sx={{ padding: theme.spacing(2, 3) }}
        title={<Typography fontWeight="fontWeightMedium">{t("global.video_recording_label")}</Typography>}
        onClose={handleOnClose}
      />
      <DialogContent>
        <Box sx={{ paddingTop: "20px" }} className="sr-no-capture">
          <MuxPlayer
            ref={videoRef}
            autoPlay
            theme="minimal"
            playbackId={video.playbackId}
            // specifies when Mux Player React Lazy loads the player
            loading="viewport"
            tokens={{ playback: videoToken.data, thumbnail: thumbnailToken.data }}
            accentColor={theme.palette.secondary1[100]}
            // without aspectRatio: 16 / 9, the player will cause a layout shift when it loads
            style={{ aspectRatio: 16 / 9 }}
          />
          {status === "error" && error ? (
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ color: "error.main" }}>
                {t(error)}
              </Typography>
            </Box>
          ) : null}
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: "12px",
            p: "16px 32px",
          }}
        >
          <DeleteExerciseRecordingVideoAction videoRef={videoRef} onDeleteVideo={onDeleteVideo} />
          <Button
            size="small"
            variant="secondary"
            startIcon={<DownloadIcon fontSize="small" />}
            onClick={onDownloadVideo}
            disabled={!videoToken.data || status === "fetching"}
          >
            {t("global.download_label")}
          </Button>
        </Box>
      </DialogContent>
    </MuiDialog>
  );
}

export default memo(ExerciseRecordingVideoDialog);
